import { supabase } from "~/integrations/supabase/client";

import type {
  ConsumptionAdjustmentResult,
  MermaBatchInput,
  MermaBatchResult,
  MermaRecord,
  OverconsumptionInput,
  OverconsumptionRecord,
} from "~/types/production";

// These RPCs were added after the last supabase types regeneration, so call them
// through a thin typed cast (same approach as productionBatchService.getIngredientCosts)
// to keep param/return typing without a full regen.
type RpcCall = <T>(
  fn: string,
  args: Record<string, unknown>
) => Promise<{ data: T | null; error: { message: string } | null }>;
const rpcCall = supabase.rpc.bind(supabase) as unknown as RpcCall;

export const consumptionAdjustmentService = {
  /** Register extra ingredient consumption (sobreconsumo) on a completed lote. */
  async recordOverconsumption(
    input: OverconsumptionInput
  ): Promise<ConsumptionAdjustmentResult> {
    const { data, error } = await rpcCall<ConsumptionAdjustmentResult>(
      "record_batch_overconsumption",
      {
        p_batch_id: input.batch_id,
        p_ingredient_type: input.ingredient_kind,
        p_oil_type_id: input.oil_type_id ?? null,
        p_raw_material_id: input.raw_material_id ?? null,
        p_extra_quantity: input.extra_quantity,
        p_unit: input.unit,
        p_plant: input.plant,
        p_notes: input.notes ?? null,
      }
    );
    if (error) throw new Error(error.message); // closed-period message surfaces here
    return data ?? { quantity: 0, unit_cost_clp: 0, total_cost_clp: 0 };
  },

  /**
   * Register one or more ingredient mermas (waste) as a single all-or-nothing
   * event. Plant + reason are shared; each line produces its own movement (still
   * individually reversible). Cost is tracked separately, never into a PT.
   */
  async recordMermas(input: MermaBatchInput): Promise<MermaBatchResult> {
    const { data, error } = await rpcCall<MermaBatchResult>(
      "record_ingredient_mermas",
      {
        p_plant: input.plant,
        p_reason: input.reason,
        p_batch_id: input.batch_id ?? null,
        p_lines: input.lines,
      }
    );
    if (error) throw new Error(error.message);
    return data ?? { count: 0, total_cost_clp: 0, lines: [] };
  },

  /** List recorded (non-reverted) mermas for the registry page. */
  async getMermas(filters?: {
    period?: string;
    plant?: string;
    ingredientId?: string;
  }): Promise<MermaRecord[]> {
    const { data, error } = await rpcCall<MermaRecord[]>(
      "get_ingredient_mermas",
      {
        p_period: filters?.period ?? null,
        p_plant: filters?.plant ?? null,
        p_ingredient_id: filters?.ingredientId ?? null,
      }
    );
    if (error) throw new Error(error.message);
    return data ?? [];
  },

  /** List active (non-reverted) sobreconsumos across batches for the registry page. */
  async getOverconsumptions(filters?: {
    period?: string;
    plant?: string;
  }): Promise<OverconsumptionRecord[]> {
    const { data, error } = await rpcCall<OverconsumptionRecord[]>(
      "get_batch_overconsumptions",
      {
        p_period: filters?.period ?? null,
        p_plant: filters?.plant ?? null,
      }
    );
    if (error) throw new Error(error.message);
    return data ?? [];
  },

  /** Reverse a sobreconsumo while its month is open (restores stock + PT cost). */
  async reverseOverconsumption(pbiId: string): Promise<void> {
    const { error } = await rpcCall("reverse_batch_overconsumption", {
      p_pbi_id: pbiId,
    });
    if (error) throw new Error(error.message);
  },

  /** Reverse a merma while its month is open (restores stock). */
  async reverseMerma(movementId: string): Promise<void> {
    const { error } = await rpcCall("reverse_ingredient_merma", {
      p_movement_id: movementId,
    });
    if (error) throw new Error(error.message);
  },
};
